<template>
  <div class="js-zoom-style">
    <div
      v-show="!useAssetAsZoom"
      class="wt-zoom-playground"
      :class="{ 'wt-on': primary }"
    >
      <img
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        ref="zoomImage"
        class="js-zoom-style"
      />
    </div>
    <img v-show="useAssetAsZoom" ref="originalImage" />
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's zoom stage vue
 */
import bus from 'AssetEditor/AssetEditorEventBus';
import PageMixin from 'AssetEditor/pages/PageMixin.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  mixins: [PageMixin],

  props: {
    active: { type: Boolean, default: false },
    primary: { type: Boolean, default: false },
  },

  data: function () {
    return {
      $jcropApi: null,
      zoomStyle: {},
      useAssetAsZoom: this.$store.state.assetEditor.assetModel.isWEBPAGE(),
    };
  },

  computed: {
    ...mapGetters('assetEditor', [
      'tmpAGD',
      'workModel',
      'story',
      'assetModel',
      'stageDimensions',
    ]),

    hasZoom: function () {
      return !_.isEmpty(this.tmpAGD.effectType);
    },

    originalAssetStyle: function () {
      let top = 0;
      let left = 0;
      let height = 0;
      let width = 0;
      const originalAssetAR =
        this.workModel.metadata.width / this.workModel.metadata.height;
      if (this.useAssetAsZoom) {
        width = app.ASSET_EDITOR_WIDTH;
        height = app.ASSET_EDITOR_WIDTH * Math.pow(originalAssetAR, -1);
      } else if (originalAssetAR > 16 / 9) {
        width = app.ASSET_EDITOR_WIDTH;
        height = width * Math.pow(originalAssetAR, -1);
        top = (app.ASSET_EDITOR_HEIGHT - height) / 2;
      } else {
        height = app.ASSET_EDITOR_HEIGHT;
        width = height * originalAssetAR;
        left = (app.ASSET_EDITOR_WIDTH - width) / 2;
      }
      return {
        top,
        left,
        height,
        width,
      };
    },

    effectType: function () {
      return this.tmpAGD.effectType;
    },

    cropValues: function () {
      return {
        scale: null,
        offset: null,
      };
    },
  },

  watch: {
    effectType: function () {
      if (this.hasZoom) {
        this.initJcrop();
      } else {
        this.destroyJcrop();
      }
    },

    cropValues: function () {
      if (this.hasZoom) {
        this.destroyJcrop();
        this.initJcrop();
      }
    },
  },

  mounted: function () {
    if (this.hasZoom) {
      this.initJcrop();
    }
    bus.$on('apply', this.apply);
  },

  methods: {
    ...mapActions('assetEditor', ['removePage', 'changeCurrentPage']),
    ...mapMutations('assetEditor', ['setAGD', 'setTMPAGD']),

    setZoomSurface: function () {
      let left = parseFloat(this.originalAssetStyle.left);
      let width = parseFloat(this.originalAssetStyle.width);
      let top = 0;
      let height = this.stageDimensions.assetEditorHeight;

      if (width > this.stageDimensions.visibleAreaWidth) {
        width = this.stageDimensions.visibleAreaWidth;
        left = this.stageDimensions.curtainWidth;
      }
      if (parseFloat(this.originalAssetStyle.height) < height) {
        top = parseFloat(this.originalAssetStyle.top);
        height = parseFloat(this.originalAssetStyle.height);
      }
      this.zoomStyle = Object.assign({}, this.zoomStyle, {
        width: width + 'px',
        height: height + 'px',
        top: top + 'px',
        left: left + 'px',
      });

      // we don't use direct style bind to prevent nextTick lag
      $(this.$el).find('.js-zoom-style').css(this.zoomStyle);
    },

    initJcrop: async function () {
      if (this.$jcropApi || !this.hasZoom) {
        return;
      }
      const jCropOptions = {
        shade: true,
        aspectRatio: this.story.aspectRatio.ratio,
        allowSelect: this.active,
        allowResize: this.active,
        allowMove: this.active,
      };

      if (!this.useAssetAsZoom) {
        this.setZoomSurface();
      }
      if (this.useAssetAsZoom) {
        const img = this.$refs.originalImage;
        await new Promise((res) => {
          img.onload = res;
          img.src = this.workModel.metadata.url;
          img.width = this.originalAssetStyle.width;
          img.height = this.originalAssetStyle.height;
        });
        this.$jcropApi = $.Jcrop(img, jCropOptions);
      } else {
        this.$jcropApi = $.Jcrop(this.$refs.zoomImage, jCropOptions);
      }
      if (this.useAssetAsZoom) {
        this.$jcropApi.ui.holder[0].style.position = 'absolute';
        this.$jcropApi.ui.holder[0].style.left =
          this.stageDimensions.curtainWidth + 'px';
      }

      if (_.isNumber(this.tmpAGD.zoomX)) {
        this.placeZoomByAGD();
      } else if (this.primary) {
        this.setDefaultZoomSelection();
      }
    },

    /** Takes zoom from AGD and applies to jcrop */
    placeZoomByAGD: function () {
      if (!this.$jcropApi) {
        return;
      }
      const imageWidth = parseFloat(this.originalAssetStyle.width);
      const imageHeight = parseFloat(this.originalAssetStyle.height);
      const assetLeft = parseFloat(this.originalAssetStyle.left);
      const assetTop = parseFloat(this.originalAssetStyle.top);
      const curtainWidth = this.stageDimensions.curtainWidth;
      const visibleAreaWidth = this.stageDimensions.visibleAreaWidth;
      const visibleAreaHeight = this.stageDimensions.assetEditorHeight;
      const fixLeft =
        imageWidth > parseFloat(this.zoomStyle.width)
          ? assetLeft - curtainWidth
          : 0;
      const fixTop =
        imageHeight > parseFloat(this.zoomStyle.height) ? assetTop : 0;
      let x1;
      let y1;
      let x2;
      let y2;

      x1 = this.tmpAGD.zoomX * imageWidth + fixLeft;
      y1 = this.tmpAGD.zoomY * imageHeight + fixTop;
      x2 = this.tmpAGD.zoomX1 * imageWidth + fixLeft;
      y2 = this.tmpAGD.zoomY1 * imageHeight + fixTop;

      // fixing zoom's out of borders issues
      // jcrop will keep the right aspect ratio...
      const previousWidth = x2 - x1;
      const previousHeight = y2 - y1;
      if (x1 < 0) {
        x1 = 0;
        if (x1 + previousWidth < visibleAreaWidth) {
          x2 = x1 + previousWidth;
        } else {
          x2 = visibleAreaWidth;
        }
      }
      if (y1 < 0) {
        y1 = 0;
        if (y1 + previousHeight < visibleAreaHeight) {
          y2 = y1 + previousHeight;
        } else {
          y2 = visibleAreaHeight;
        }
      }

      if (x2 > visibleAreaWidth) {
        x2 = visibleAreaWidth;
        if (x2 - previousWidth > 0) {
          x1 = x2 - previousWidth;
        } else {
          x1 = 0;
        }
      }

      if (y2 > visibleAreaHeight && !this.useAssetAsZoom) {
        y2 = visibleAreaHeight;
        y1 = y2 - previousHeight;
        if (y2 - previousHeight > 0) {
          y1 = y2 - previousHeight;
        } else {
          y1 = 0;
        }
      }

      this.$jcropApi.setSelect([x1, y1, x2, y2]);
      this.setTmpAGDByJcrop();
    },

    backToPreview() {
      if (this.$jcropApi) {
        this.setTmpAGDByJcrop();
      }
      this.askSaveChanges(this.isTempAgdDirty);
    },

    apply: function () {
      if (!this.active) {
        return;
      }
      if (!this.hasZoom) {
        this.removePage('zoom');
      } else if (this.$jcropApi) {
        this.setTmpAGDByJcrop();
        this.setAGD({
          effectType: this.effectType || 'ZOOM_IN',
          zoomX: this.tmpAGD.zoomX,
          zoomY: this.tmpAGD.zoomY,
          zoomX1: this.tmpAGD.zoomX1,
          zoomY1: this.tmpAGD.zoomY1,
        });
      }
      this.changeCurrentPage('preview');
    },

    setTmpAGDByJcrop: function () {
      const selection = this.$jcropApi.tellSelect();
      const assetWidth = parseFloat(this.originalAssetStyle.width);
      const assetHeight = parseFloat(this.originalAssetStyle.height);
      const assetLeft = parseFloat(this.originalAssetStyle.left);
      const assetTop = parseFloat(this.originalAssetStyle.top);
      const cropAreaLeft = parseFloat(this.zoomStyle.left) || 0;
      const cropAreaTop = parseFloat(this.zoomStyle.top) || 0;
      const yFix = this.useAssetAsZoom ? 0 : cropAreaTop - (assetTop || 0);
      const xFix = this.useAssetAsZoom ? 0 : cropAreaLeft - (assetLeft || 0);
      // scale = (this.viewState.getAGD('scale') || 1);

      this.setTMPAGD({
        zoomX: (selection.x + xFix) / assetWidth,
        zoomY: (selection.y + yFix) / assetHeight,
        zoomX1: (selection.x2 + xFix) / assetWidth,
        zoomY1: (selection.y2 + yFix) / assetHeight,
      });
    },

    setDefaultZoomSelection: function () {
      if (this.useAssetAsZoom) {
        return;
      }
      const selection = {};
      const selectionOffset = 0.66;
      const imgWidth = parseFloat(this.zoomStyle.width);
      const imgHeight = parseFloat(this.zoomStyle.height);
      const jCropHeight = imgHeight * selectionOffset;
      const jCropWidth = jCropHeight * this.story.aspectRatio.ratio;

      selection.x = (imgWidth - jCropWidth) / 2;
      selection.y = (imgHeight - jCropHeight) / 2;

      selection.x2 = jCropWidth + selection.x;
      selection.y2 = jCropHeight + selection.y;

      this.$jcropApi.setSelect([
        selection.x,
        selection.y,
        selection.x2,
        selection.y2,
      ]);
    },

    disableJcrop: function () {
      if (this.$jcropApi) {
        this.$jcropApi.disable();
      }
    },

    enableJcrop: function () {
      if (this.$jcropApi) {
        this.$jcropApi.enable();
      }
    },

    destroyJcrop: function () {
      if (this.$jcropApi) {
        this.$jcropApi.destroy();
      }
      this.$jcropApi = null;
    },
  },

  beforeDestroy: function () {
    this.destroyJcrop();
    bus.$off('apply', this.apply);
  },
};
</script>
